import { useRef, useEffect, useState, useCallback } from "react";
import { Dialog } from "radix-ui";
import { Button } from "@/components/ui/button";
import { X, Undo2, Trash2, Brush, Eraser } from "lucide-react";
import { strokesToMaskPixels, hasPaintedPixels } from "@/lib/maskCanvas";

const COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#3b82f6", // blue
  "#8b5cf6", // purple
  "#ec4899", // pink
  "#ffffff", // white
];

interface DrawingEditorProps {
  open: boolean;
  imageSrc: string;
  /** "draw" paints on the image itself, "mask" paints a removal mask over it */
  mode?: "draw" | "mask";
  /** In draw mode: the edited image. In mask mode: the black-and-white mask. */
  onSave: (file: File, preview: string) => void;
  onClose: () => void;
}

export function DrawingEditor({ open, imageSrc, mode = "draw", onSave, onClose }: DrawingEditorProps) {
  const isMask = mode === "mask";
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [brushSize, setBrushSize] = useState(isMask ? 40 : 5);
  const [brushColor, setBrushColor] = useState("#ef4444");
  const [tool, setTool] = useState<"brush" | "eraser">("brush");
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState<ImageData[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const naturalSize = useRef({ width: 0, height: 0 });

  // In mask mode the image is a background layer and strokes live on the canvas alone
  const [backgroundSrc, setBackgroundSrc] = useState<string | null>(null);

  // Load image onto canvas when dialog opens
  useEffect(() => {
    if (!open || !imageSrc) return;
    setHistory([]);
    setImageLoaded(false);
    setHasStrokes(false);
    setTool("brush");

    const img = new Image();
    if (!imageSrc.startsWith("blob:")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      naturalSize.current = { width: img.naturalWidth, height: img.naturalHeight };
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (isMask) {
        // Keep the canvas transparent — the image shows through from behind
        setBackgroundSrc(imageSrc);
      } else {
        setBackgroundSrc(null);
        ctx.drawImage(img, 0, 0);
      }
      // Save initial state
      setHistory([ctx.getImageData(0, 0, canvas.width, canvas.height)]);
      setImageLoaded(true);
    };
    img.src = imageSrc;
  }, [open, imageSrc, isMask]);

  const getCanvasPoint = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  const startDraw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const rect = canvas.getBoundingClientRect();
    const scale = canvas.width / rect.width;

    const point = getCanvasPoint(e);
    ctx.globalCompositeOperation = isMask && tool === "eraser" ? "destination-out" : "source-over";
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    ctx.strokeStyle = isMask ? "#ffffff" : brushColor;
    ctx.lineWidth = brushSize * scale;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    setIsDrawing(true);
    canvas.setPointerCapture(e.pointerId);
  }, [brushColor, brushSize, getCanvasPoint, isMask, tool]);

  const draw = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const point = getCanvasPoint(e);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  }, [isDrawing, getCanvasPoint]);

  const endDraw = useCallback(() => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.closePath();
    ctx.globalCompositeOperation = "source-over";
    // Push to history
    const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setHistory((prev) => [...prev, snapshot]);
    if (isMask) setHasStrokes(hasPaintedPixels(snapshot.data));
  }, [isDrawing, isMask]);

  const handleUndo = useCallback(() => {
    if (history.length <= 1) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    const newHistory = history.slice(0, -1);
    const prevState = newHistory[newHistory.length - 1]!;
    ctx.putImageData(prevState, 0, 0);
    setHistory(newHistory);
    if (isMask) setHasStrokes(hasPaintedPixels(prevState.data));
  }, [history, isMask]);

  const handleClear = useCallback(() => {
    if (history.length === 0) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    // Restore initial state
    const initial = history[0]!;
    ctx.putImageData(initial, 0, 0);
    setHistory([initial]);
    if (isMask) setHasStrokes(false);
  }, [history, isMask]);

  const handleSave = useCallback(() => {
    const canvas = canvasRef.current!;

    if (isMask) {
      // Flatten the translucent strokes into a strict black-and-white mask
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
      const strokes = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      const maskCtx = maskCanvas.getContext("2d")!;
      maskCtx.putImageData(
        new ImageData(strokesToMaskPixels(strokes.data), canvas.width, canvas.height),
        0,
        0
      );
      maskCanvas.toBlob((blob) => {
        if (!blob) return;
        onSave(new File([blob], "mask.png", { type: "image/png" }), maskCanvas.toDataURL("image/png"));
      }, "image/png");
      return;
    }

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "edited-image.png", { type: "image/png" });
      const preview = canvas.toDataURL("image/png");
      onSave(file, preview);
    }, "image/png");
  }, [onSave, isMask]);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex flex-col outline-none"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {/* Toolbar */}
          <div className="flex items-center gap-3 px-4 py-2 shrink-0 border-b border-border" style={{ backgroundColor: "var(--effect-toolbar-bg)" }}>
            <Dialog.Title className="text-sm font-medium mr-auto text-foreground">
              {isMask ? "Označi šta se uklanja" : "Crtanje na slici"}
            </Dialog.Title>

            {isMask ? (
              /* Brush / eraser toggle */
              <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setTool("brush")}
                  className={tool === "brush" ? "bg-primary text-primary-foreground hover:bg-primary/90" : "text-foreground hover:bg-secondary"}
                  title="Kist"
                >
                  <Brush className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setTool("eraser")}
                  className={tool === "eraser" ? "bg-primary text-primary-foreground hover:bg-primary/90" : "text-foreground hover:bg-secondary"}
                  title="Gumica"
                >
                  <Eraser className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              /* Color palette */
              <div className="flex items-center gap-1">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setBrushColor(color)}
                    className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: color,
                      borderColor: brushColor === color ? "var(--effect-brush-active-border)" : "transparent",
                      transform: brushColor === color ? "scale(1.15)" : undefined,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Brush size */}
            <div className="flex items-center gap-2 ml-2">
              <span className="text-xs text-muted-foreground">Veličina</span>
              <input
                type="range"
                min={1}
                max={isMask ? 200 : 20}
                value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-24 accent-primary"
              />
              <span className="text-xs w-8 text-center text-foreground">{brushSize}{isMask ? "px" : ""}</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 ml-2 border-l border-border pl-3">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleUndo}
                disabled={history.length <= 1}
                className="text-foreground hover:bg-secondary disabled:opacity-30"
                title="Poništi"
              >
                <Undo2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleClear}
                disabled={history.length <= 1}
                className="text-foreground hover:bg-secondary disabled:opacity-30"
                title={isMask ? "Obriši masku" : "Obriši crteže"}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            {/* Save / Close */}
            <div className="flex items-center gap-2 ml-2 border-l border-border pl-3">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isMask && !hasStrokes}
                className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                style={{ boxShadow: "var(--effect-glow-sm)" }}
                title={isMask && !hasStrokes ? "Prvo označi područje koje se uklanja" : undefined}
              >
                {isMask ? "Koristi masku" : "Sačuvaj"}
              </Button>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon-sm" className="text-foreground hover:bg-secondary">
                  <X className="w-4 h-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* Canvas area */}
          <div ref={containerRef} className="flex-1 flex items-center justify-center overflow-hidden p-4" style={{ backgroundColor: "var(--effect-canvas-bg)" }}>
            {!imageLoaded && (
              <div className="text-muted-foreground text-sm absolute">Učitavanje slike...</div>
            )}
            <div className={`relative max-w-full max-h-full ${imageLoaded ? "" : "invisible"}`}>
              {backgroundSrc && (
                <img
                  src={backgroundSrc}
                  alt=""
                  className="max-w-full max-h-full object-contain rounded select-none pointer-events-none"
                  style={{ maxHeight: "calc(100vh - 8rem)" }}
                  draggable={false}
                />
              )}
              <canvas
                ref={canvasRef}
                onPointerDown={startDraw}
                onPointerMove={draw}
                onPointerUp={endDraw}
                onPointerLeave={endDraw}
                className={
                  backgroundSrc
                    ? "absolute inset-0 w-full h-full rounded cursor-crosshair touch-none"
                    : "max-w-full max-h-full object-contain rounded cursor-crosshair touch-none"
                }
                style={backgroundSrc ? { opacity: 0.55 } : { imageRendering: "auto" }}
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
